import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { X, ArrowLeft, ArrowRight } from 'lucide-react';
import { api } from '../../services/api.js';
import type { PoS } from '../../types';
import { extractApiError } from '../../utils/pdfDownload';
import { computePromoSplit } from '../../utils/promotion';
import { berlinTodayIso } from '../../utils/datetime';

interface EligibleOrder {
  id: string;
  pos_id: string;
  pos_name: string;
  pos_city: string;
  product_name: string;
  manufacturer_name: string;
  variant_name?: string;
  quantity: number;
  unit_price: number;
  pfand_per_case: number;
  promotion_type?: string | null;
  status: string;
  created_at: string;
}

interface NewLieferscheinModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}

export const NewLieferscheinModal: React.FC<NewLieferscheinModalProps> = ({
  open,
  onClose,
  onCreated,
}) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [pos, setPos] = useState<PoS[]>([]);
  const [posId, setPosId] = useState('');
  const [posSearch, setPosSearch] = useState('');
  const [eligible, setEligible] = useState<EligibleOrder[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<Set<string>>(new Set());
  const [loadingEligible, setLoadingEligible] = useState(true);
  const [deliveryDate, setDeliveryDate] = useState(berlinTodayIso());
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    void loadPos();
    setStep(1);
    setPosId('');
    setPosSearch('');
    setEligible([]);
    setSelectedOrders(new Set());
    setNotes('');
    setDeliveryDate(berlinTodayIso());
  }, [open]);

  useEffect(() => {
    if (step === 2 && posId) {
      setLoadingEligible(true);
      void loadEligible(posId);
    }
  }, [step, posId]);

  const loadPos = async () => {
    try {
      const res = await api.get('/pos?limit=1000');
      const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setPos(list);
    } catch {
      toast.error('Failed to load PoS list');
    }
  };

  const loadEligible = async (id: string) => {
    setLoadingEligible(true);
    try {
      const res = await api.get(`/orders/lieferschein-eligible?pos_id=${id}`);
      setEligible(res.data);
      setSelectedOrders(new Set((res.data as EligibleOrder[]).map((o) => o.id)));
    } catch {
      toast.error('Failed to load eligible order lines');
      setEligible([]);
    } finally {
      setLoadingEligible(false);
    }
  };

  const filteredPos = useMemo(() => {
    const term = posSearch.trim().toLowerCase();
    const sorted = pos.slice().sort((a, b) => a.name.localeCompare(b.name));
    if (!term) return sorted;
    return sorted.filter(
      (p) =>
        p.name.toLowerCase().includes(term) ||
        (p.city || '').toLowerCase().includes(term)
    );
  }, [pos, posSearch]);

  const toggleOrder = (id: string) => {
    const next = new Set(selectedOrders);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedOrders(next);
  };

  const totals = useMemo(() => {
    const picked = eligible.filter((o) => selectedOrders.has(o.id));
    const net = picked.reduce((s, o) => {
      const split = computePromoSplit(o.quantity, o.promotion_type);
      return s + Number(o.unit_price || 0) * split.paid;
    }, 0);
    const pfand = picked.reduce((s, o) => {
      const split = computePromoSplit(o.quantity, o.promotion_type);
      return s + Number(o.pfand_per_case || 0) * split.delivered;
    }, 0);
    return { net, pfand, gross: net + pfand, count: picked.length };
  }, [eligible, selectedOrders]);

  const canProceedFromStep2 = selectedOrders.size > 0;

  const handleSubmit = async () => {
    if (selectedOrders.size === 0 || submitting) return;
    setSubmitting(true);
    try {
      const res = await api.post('/lieferscheine', {
        pos_id: posId,
        delivery_date: deliveryDate,
        order_ids: Array.from(selectedOrders),
        notes: notes.trim() || undefined,
      });
      toast.success(`Delivery note ${res.data.lieferschein_number} created`);
      onCreated(res.data.id);
      onClose();
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(`Create failed: ${message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const selectedPos = pos.find((p) => p.id === posId);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-xl font-bold text-gray-900">New delivery note</h2>
            <p className="text-xs text-gray-500 mt-0.5">Step {step} of 3</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X size={20} />
          </button>
        </div>

        <div className="px-5 pt-3">
          <div className="flex gap-1">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full ${
                  step >= i ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>

        <div className="p-5 space-y-4">
          {step === 1 && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">
                  Pick PoS
                </label>
                <input
                  type="text"
                  value={posSearch}
                  onChange={(e) => setPosSearch(e.target.value)}
                  placeholder="Search by name or city..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-2 focus:ring-2 focus:ring-blue-500"
                />
                <div className="border border-gray-200 rounded-lg max-h-72 overflow-y-auto">
                  {filteredPos.length === 0 ? (
                    <p className="p-4 text-sm text-gray-500 text-center">No PoS found</p>
                  ) : (
                    filteredPos.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => setPosId(p.id)}
                        className={`w-full text-left px-3 py-2 border-b border-gray-100 last:border-b-0 hover:bg-blue-50 transition-colors ${
                          posId === p.id ? 'bg-blue-100' : ''
                        }`}
                      >
                        <div className="font-bold text-gray-900">{p.name}</div>
                        <div className="text-xs text-gray-500">
                          {p.street ? `${p.street} ${p.street_number || ''}, ` : ''}
                          {p.postal_code} {p.city}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <p className="text-sm text-gray-600 mb-3">
                Select order lines for{' '}
                <span className="font-bold text-gray-900">{selectedPos?.name}</span>.
                Only self-distributor order lines not yet on a delivery note are shown.
              </p>
              {loadingEligible ? (
                <p className="text-sm text-gray-500 py-8 text-center">Loading...</p>
              ) : eligible.length === 0 ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm">
                  <p className="font-bold text-yellow-900">No eligible order lines</p>
                  <p className="text-yellow-800 mt-1">
                    This PoS has no self-distributor order lines waiting for delivery.
                    Create an order line first via the Order lines page.
                  </p>
                </div>
              ) : (
                <>
                  <div className="border border-gray-200 rounded-lg max-h-72 overflow-y-auto divide-y divide-gray-100">
                    {eligible.map((o) => {
                      const checked = selectedOrders.has(o.id);
                      const split = computePromoSplit(o.quantity, o.promotion_type);
                      const lineTotal = Number(o.unit_price || 0) * split.paid;
                      const hasPromo =
                        !!o.promotion_type && o.promotion_type !== 'none' && split.free > 0;
                      return (
                        <label
                          key={o.id}
                          className={`flex items-start gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 ${
                            checked ? 'bg-blue-50' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleOrder(o.id)}
                            className="mt-1"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-gray-900 truncate">
                              {o.product_name}
                              {o.variant_name ? ` (${o.variant_name})` : ''}
                              {hasPromo && (
                                <span className="ml-2 inline-flex items-center text-[10px] font-black uppercase text-yellow-800 bg-yellow-100 px-1.5 py-0.5 rounded">
                                  {o.promotion_type}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">
                              {o.manufacturer_name} |{' '}
                              {hasPromo
                                ? `deliver ${split.delivered} (${split.paid} paid + ${split.free} free)`
                                : `qty ${split.delivered}`}{' '}
                              | {Number(o.unit_price || 0).toFixed(2)} EUR/case | {o.status}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-gray-900">
                              {lineTotal.toFixed(2)} EUR
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 mt-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{totals.count} order line(s) selected</span>
                      <span className="font-bold">Net {totals.net.toFixed(2)} EUR</span>
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-gray-600">Pfand</span>
                      <span className="font-bold">{totals.pfand.toFixed(2)} EUR</span>
                    </div>
                    <div className="flex justify-between border-t border-gray-200 pt-2 mt-2">
                      <span className="font-bold text-gray-900">Gross</span>
                      <span className="font-bold">{totals.gross.toFixed(2)} EUR</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">
                  Delivery date *
                </label>
                <input
                  type="date"
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Optional notes printed on the delivery note"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600">PoS</span>
                  <span className="font-bold">{selectedPos?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Order lines</span>
                  <span className="font-bold">{totals.count}</span>
                </div>
                <div className="flex justify-between border-t border-gray-200 pt-2 mt-2">
                  <span className="font-bold text-gray-900">Total</span>
                  <span className="font-bold">{totals.gross.toFixed(2)} EUR</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-5 border-t border-gray-200 flex justify-between items-center sticky bottom-0 bg-white">
          <button
            onClick={() => (step === 1 ? onClose() : setStep((s) => (s - 1) as 1 | 2 | 3))}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-bold hover:bg-gray-50"
          >
            {step === 1 ? 'Cancel' : (
              <>
                <ArrowLeft size={16} />
                Back
              </>
            )}
          </button>
          {step < 3 ? (
            <button
              onClick={() => setStep((s) => (s + 1) as 1 | 2 | 3)}
              disabled={(step === 1 && !posId) || (step === 2 && !canProceedFromStep2)}
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Next
              <ArrowRight size={16} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting || selectedOrders.size === 0 || !deliveryDate}
              className="px-5 py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {submitting ? 'Creating...' : 'Create delivery note'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
